import { preventBubblingEvent } from '@mote/helpers/browser'
import { Slider, VisuallyHidden } from '@mote/ui'
import React, { useState } from 'react'
import ClickAwayListener from 'react-click-away-listener'
import { animated, useSpring } from 'react-spring'
import styled, { css } from 'styled-components'
import { VolumeMuteButton } from './Controls'
import { useMotePlayer } from '../contexts/motePlayer.context'

const TRANSITION_DURATION = 150
const CONTAINER_SPRING_VALUES = {
  initial: {
    height: '0px',
    opacity: 0
  },
  final: {
    height: '85px',
    opacity: 1
  }
}

export const VolumeRich = ({ disabled = false }) => {
  const {
    onVolumeChange: changeVolume,
    toggleMute,
    muted,
    internalVolume: volume
  } = useMotePlayer()

  const [{ height, opacity }, set] = useSpring(() => ({
    ...CONTAINER_SPRING_VALUES.initial,
    config: { duration: 150 }
  }))

  const isSliderFocusedRef = React.useRef(false)
  const isHoveringRef = React.useRef(false)
  const openTimeoutId = React.useRef(null)
  const closeTimeoutId = React.useRef(null)
  const [volumeOpen, setVolumeOpen] = useState(false)

  const toggleAnimation = React.useCallback(open => {
    set(open ? CONTAINER_SPRING_VALUES.final : CONTAINER_SPRING_VALUES.initial)

    setVolumeOpen(open)
  }, [])

  const manualToggle = React.useCallback(() => {
    if (volumeOpen) {
      toggleMute()
      return
    }

    toggleAnimation(true)
  }, [volumeOpen, toggleAnimation, toggleMute])

  const handleVolumeMouseEnter = React.useCallback(() => {
    isHoveringRef.current = true

    openTimeoutId.current = setTimeout(
      () => toggleAnimation(true),
      TRANSITION_DURATION
    )
  }, [toggleAnimation])

  const handleVolumeMouseLeave = React.useCallback(() => {
    isHoveringRef.current = false

    if (openTimeoutId.current) {
      clearTimeout(openTimeoutId.current)
      openTimeoutId.current = null
    }

    closeTimeoutId.current = setTimeout(() => {
      if (isHoveringRef.current === false) {
        toggleAnimation(false)
      }
    }, TRANSITION_DURATION)
  }, [toggleAnimation])

  const handleSliderContainerMouseEnter = React.useCallback(() => {
    isHoveringRef.current = true
  }, [])

  const handleSliderContainerMouseLeave = React.useCallback(() => {
    isHoveringRef.current = false

    closeTimeoutId.current = setTimeout(
      () => toggleAnimation(false),
      TRANSITION_DURATION
    )
  }, [toggleAnimation])

  const handleSliderOnFocus = React.useCallback(() => {
    if (volumeOpen) return

    isSliderFocusedRef.current = true
    toggleAnimation(true)
  }, [volumeOpen, toggleAnimation])

  const handleSliderOnBlur = React.useCallback(() => {
    if (!isSliderFocusedRef.current) return

    isSliderFocusedRef.current = false

    if (!isHoveringRef.current && volumeOpen) toggleAnimation(false)
  }, [volumeOpen, toggleAnimation])

  React.useEffect(() => {
    return () => {
      if (openTimeoutId.current) clearTimeout(openTimeoutId.current)
      if (closeTimeoutId.current) clearTimeout(closeTimeoutId.current)
    }
  }, [])

  return (
    <Root disabled={disabled}>
      <VisuallyHidden>{`Volume: ${volume}%`}</VisuallyHidden>
      <VolumeActivator
        onMouseEnter={handleVolumeMouseEnter}
        onMouseLeave={handleVolumeMouseLeave}
        tabIndex={-1}
        aria-expanded={volumeOpen}
        aria-controls='mote-volume-slider'
      >
        <StyledVolumeMuteButton
          size={36}
          variant='rich'
          aria-label={`${muted ? 'Unmute' : 'Mute'} (press m)`}
          tooltipProps={{ direction: 'down' }}
          isMuted={muted}
          onClick={
            !disabled
              ? e => {
                  e.stopPropagation()
                  toggleMute()
                }
              : null
          }
          onTouchEnd={
            !disabled
              ? e => {
                  preventBubblingEvent(e)
                  manualToggle()
                }
              : null
          }
          useNewIcon
        />
      </VolumeActivator>
      <ClickAwayListener onClickAway={() => toggleAnimation(false)}>
        <SliderContainer
          id='mote-volume-slider'
          aria-hidden={!volumeOpen}
          onMouseEnter={handleSliderContainerMouseEnter}
          onMouseLeave={handleSliderContainerMouseLeave}
          style={{
            height,
            opacity
          }}
        >
          <Slider
            aria-label='Volume'
            orientation='vertical'
            value={volume}
            min={0}
            max={100}
            step={10}
            getAriaValueText={value => `${value}% volume`}
            onChange={changeVolume}
            onFocus={handleSliderOnFocus}
            onBlur={handleSliderOnBlur}
            focusThumbOnChange={false}
            isDisabled={disabled}
          >
            <StyledSliderTrack>
              <StyledSliderTrackFilled />
            </StyledSliderTrack>
            <StyledSliderThumb />
          </Slider>
        </SliderContainer>
      </ClickAwayListener>
    </Root>
  )
}

const Root = styled.div`
  position: relative;

  ${({ disabled }) =>
    disabled &&
    css`
      cursor: not-allowed;
      opacity: 0.4;
    `};
`

const SliderContainer = animated(
  styled.div`
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 12px 0;
    width: 24px;
    height: 0;
    border-radius: 50%;
    border-radius: 12px;
    box-sizing: border-box;
    margin-bottom: 10px;
    left: 50%;
    bottom: 100%;
    transform: translateX(-50%);
    background: rgba(33, 34, 40, 0.4);
  `
)

const VolumeActivator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  box-sizing: border-box;
`

const StyledSliderTrack = styled(Slider.Track)`
  width: 3px;
  border-radius: 1.5px;
  background: rgb(255 255 255 / 30%);
`

const StyledSliderTrackFilled = styled(Slider.TrackFilled)`
  background: #ffffff;
  border-radius: 1.5px;
`

const StyledSliderThumb = styled(Slider.Thumb)`
  width: 14px;
  height: 14px;
  box-shadow: none;

  &:focus,
  &[data-focus] {
    box-shadow: 0 0 0 2px rgba(47, 166, 255, 0.6);
  }

  &:disabled,
  &[data-disabled] {
    background: #d1d1e0;
  }

  ${({ isDisabled }) =>
    isDisabled &&
    css`
      background: #d1d1e0;
    `};
`

const StyledVolumeMuteButton = styled(VolumeMuteButton)`
  svg {
    height: 24px;
    width: 24px;
  }
`
